import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import type { SessionState } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { useSession, useSetSession } from '../lib/session';
import { AuthLayout } from '../components/AuthLayout';
import { Alert, Button, Field } from '../components/ui';
import { safeNext } from '../router';

export function LoginPage() {
  const { data } = useSession();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const navigate = useNavigate();
  const setSession = useSetSession();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');

  const login = useMutation({
    mutationFn: () => api.post<SessionState>('/auth/login', { email, password }),
    onSuccess: (s) => {
      setPassword('');
      setSession(s);
      if (s.status === 'signed_in') navigate(next, { replace: true });
    },
  });

  if (data?.status === 'mfa_required') return <MfaStep next={next} />;

  const fields = fieldErrors(login.error);
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <AuthLayout
      title="Sign in"
      description="Plan and run your training sessions."
      footer={
        <>
          New to Hoopify?{' '}
          <Link to={`/signup${params.get('next') ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-action hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {login.isError && !Object.keys(fields).length && <Alert>{errorMessage(login.error)}</Alert>}
        <Field label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} error={fields.email} required autoFocus />
        <div>
          <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={fields.password} required />
          <Link to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`} className="mt-2 inline-block text-[13px] text-muted hover:text-ink hover:underline">
            Forgotten your password?
          </Link>
        </div>
        <Button type="submit" loading={login.isPending} className="btn-primary btn-lg w-full">
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}

function MfaStep({ next }: { next: string }) {
  const navigate = useNavigate();
  const setSession = useSetSession();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);

  const verify = useMutation({
    mutationFn: () => api.post<SessionState>('/auth/login/mfa', { code }),
    onSuccess: (s) => {
      setSession(s);
      navigate(next, { replace: true });
    },
    onError: () => setCode(''),
  });
  const cancel = useMutation({
    mutationFn: () => api.post<SessionState>('/auth/logout'),
    onSuccess: (s) => setSession(s),
  });

  return (
    <AuthLayout
      title="Two-step sign-in"
      description={recovery ? 'Enter one of the recovery codes you saved when you turned on two-step sign-in. Each code works once.' : 'Enter the 6-digit code from your authenticator app.'}
      footer={
        <div className="flex flex-wrap justify-between gap-2">
          <button className="hover:text-ink hover:underline" onClick={() => { setRecovery(!recovery); setCode(''); verify.reset(); }}>
            {recovery ? 'Use my authenticator app' : 'Lost your phone? Use a recovery code'}
          </button>
          <button className="hover:text-ink hover:underline" onClick={() => cancel.mutate()}>
            Cancel
          </button>
        </div>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); verify.mutate(); }} className="space-y-4" noValidate>
        {verify.isError && <Alert>{errorMessage(verify.error)}</Alert>}
        <Field
          key={recovery ? 'r' : 't'}
          label={recovery ? 'Recovery code' : 'Code'}
          value={code}
          onChange={(e) => setCode(recovery ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode={recovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          placeholder={recovery ? 'xxxxx-xxxxx' : '123456'}
          className="field-input tnum tracking-[0.2em]"
          autoFocus
          required
        />
        <Button type="submit" loading={verify.isPending} disabled={recovery ? code.trim().length < 10 : code.length !== 6} className="btn-primary btn-lg w-full">
          Continue
        </Button>
      </form>
    </AuthLayout>
  );
}
